import "./index.css";
import { useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, Link, useNavigate } from "react-router";
import { Toaster } from "sonner";
import { authClient } from "@/lib/auth-client";
import { queryClient } from "@/lib/query-client";
import { Login } from "@/pages/Login";
import { Dashboard } from "@/pages/Dashboard";
import { MenuItems } from "@/pages/MenuItems";
import { MenuItemForm } from "@/pages/MenuItemForm";
import { Orders } from "@/pages/Orders";
import { OrderDetail } from "@/pages/OrderDetail";
import { Categories } from "@/pages/Categories";
import { LayoutDashboard, UtensilsCrossed, Tag, ShoppingBag, LogOut, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

function AuthGuard() {
  const { data: session, isPending } = authClient.useSession();

  if (isPending) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
          <span className="text-[13px] text-muted-foreground">Loading</span>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

const navItems = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/orders", label: "Orders", icon: ShoppingBag },
  { to: "/menu", label: "Menu", icon: UtensilsCrossed },
  { to: "/categories", label: "Categories", icon: Tag },
];

function SidebarLink({ to, label, icon: Icon, onNavigate }: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; onNavigate?: () => void }) {
  const location = useLocation();
  const isActive = to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn(
        "group flex items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium transition-all duration-150",
        isActive
          ? "bg-foreground text-background"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
    >
      <Icon className="size-4 shrink-0" />
      {label}
    </Link>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    if (isSigningOut) return;

    setIsSigningOut(true);
    try {
      await authClient.signOut();
      navigate("/login", { replace: true });
    } finally {
      setIsSigningOut(false);
    }
  };

  return (
    <>
      {/* Logo */}
      <div className="flex h-14 items-center px-5">
        <Link to="/" className="flex items-center gap-2.5" onClick={onNavigate}>
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-foreground">
            <span className="text-[11px] font-bold text-background">T</span>
          </div>
          <span className="text-[14px] font-semibold tracking-tight">Miss T's</span>
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex flex-1 flex-col gap-1 px-3 pt-2">
        {navItems.map((item) => (
          <SidebarLink key={item.to} {...item} onNavigate={onNavigate} />
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t p-3">
        <button
          onClick={handleSignOut}
          disabled={isSigningOut}
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
        >
          <LogOut className="size-4" />
          {isSigningOut ? "Signing out..." : "Sign out"}
        </button>
      </div>
    </>
  );
}

function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-[240px] shrink-0 flex-col border-r bg-sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setSidebarOpen(false)}>
          <div className="absolute inset-0 bg-black/60" />
          <aside
            className="relative flex h-full w-[240px] flex-col bg-sidebar"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSidebarOpen(false)}
              className="absolute top-4 right-4 rounded-md p-1 text-muted-foreground hover:text-foreground"
            >
              <X className="size-5" />
            </button>
            <SidebarContent onNavigate={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile top bar */}
        <div className="flex h-14 items-center gap-3 border-b px-4 md:hidden">
          <button onClick={() => setSidebarOpen(true)} className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <Menu className="size-5" />
          </button>
          <span className="text-[14px] font-semibold tracking-tight">Miss T's</span>
        </div>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-5xl px-4 py-4 md:px-8 md:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: "#0a0a0a",
              color: "#fafafa",
              border: "none",
              fontSize: "13px",
              borderRadius: "8px",
            },
          }}
        />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<AuthGuard />}>
            <Route element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="menu" element={<MenuItems />} />
              <Route path="menu/new" element={<MenuItemForm />} />
              <Route path="menu/:id/edit" element={<MenuItemForm />} />
              <Route path="categories" element={<Categories />} />
              <Route path="orders" element={<Orders />} />
              <Route path="orders/:id" element={<OrderDetail />} />
            </Route>
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
